<template>
  <FlashNotifications ref="flash" />

  <div class="h-full flex flex-col min-h-0">
    <!-- First load -->
    <div v-if="!store.loaded && store.loading" class="flex-1 min-h-0 flex animate-pulse">
      <div class="w-full lg:w-[380px] lg:flex-shrink-0 lg:border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] divide-y divide-gray-100 dark:divide-gray-800">
        <div v-for="n in 12" :key="n" class="flex gap-2.5 px-3 py-2">
          <div class="w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
          <div class="flex-1 space-y-1.5 pt-0.5">
            <div class="h-3.5 w-4/5 rounded bg-gray-200 dark:bg-gray-700"></div>
            <div class="h-3 w-3/5 rounded bg-gray-100 dark:bg-gray-800"></div>
            <div class="h-2.5 w-2/5 rounded bg-gray-100 dark:bg-gray-800"></div>
          </div>
        </div>
      </div>
      <div class="hidden lg:block flex-1 bg-white dark:bg-[#1C2128]">
        <div class="max-w-3xl mx-auto px-5 sm:px-6">
          <div class="flex gap-3.5 pt-5 pb-5">
            <div class="w-11 h-11 rounded-xl bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
            <div class="flex-1 space-y-2.5 pt-0.5"><div class="h-5 w-3/4 rounded bg-gray-200 dark:bg-gray-700"></div><div class="h-3.5 w-1/3 rounded bg-gray-100 dark:bg-gray-800"></div></div>
          </div>
          <hr class="border-gray-200 dark:border-gray-700">
          <div class="py-5 space-y-3"><div v-for="n in 6" :key="n" class="h-3.5 rounded bg-gray-100 dark:bg-gray-800" :style="{ width: `${96 - n * 6}%` }"></div></div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="store.error && !store.loaded" class="flex-1 flex flex-col items-center justify-center py-24 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.error }}</p>
      <button @click="store.fetchJobs" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Try again</button>
    </div>

    <!-- Nothing collected yet -->
    <div v-else-if="store.loaded && store.counts.all === 0" class="flex-1 overflow-y-auto">
      <div class="max-w-xl mx-auto px-6 py-16 text-center">
        <div class="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mx-auto mb-5">
          <svg class="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>
        </div>
        <h2 class="text-lg font-bold text-gray-900 dark:text-white">No jobs yet</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">Jobs the extension collects show up here. Two steps to get the first ones.</p>

        <ol class="text-left space-y-3 mb-7">
          <li class="flex gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shadow-sm p-4">
            <span class="w-6 h-6 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
            <span class="text-sm text-gray-700 dark:text-gray-200">Add the job titles and locations you want on the <span class="font-semibold">Searches</span> tab.</span>
          </li>
          <li class="flex gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shadow-sm p-4">
            <span class="w-6 h-6 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <span class="text-sm text-gray-700 dark:text-gray-200">Open the Timelydo Jobs extension and press <span class="font-semibold">Start</span> on a search.</span>
          </li>
        </ol>
        <button type="button" @click="$emit('go-searches')" class="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm">
          Set up searches
        </button>
      </div>
    </div>

    <!-- The jobs -->
    <div v-else class="flex-1 min-h-0 flex">
      <!-- List -->
      <div class="w-full lg:w-[380px] lg:flex-shrink-0 lg:border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] flex flex-col min-h-0">
        <div class="flex-shrink-0 flex items-center justify-between px-3 h-8 text-[11px] border-b border-gray-100 dark:border-gray-800" aria-live="polite">
          <span class="font-semibold text-gray-700 dark:text-gray-200 tabular-nums">
            <template v-if="store.loading && !store.refreshing">Loading...</template>
            <template v-else>{{ store.pageInfo.total_records }} {{ store.pageInfo.total_records === 1 ? 'job' : 'jobs' }}<template v-if="store.isFiltered"> found</template></template>
          </span>
          <span class="text-gray-400 dark:text-gray-500">{{ store.filters.sort === 'newest' ? 'Newest posted first' : 'Recently saved first' }}</span>
        </div>

        <div ref="scroller" class="flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div v-if="store.jobs.length === 0" class="px-6 py-16 text-center">
            <p class="font-semibold text-gray-700 dark:text-gray-200">{{ empty.title }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">{{ empty.body }}</p>
            <button v-if="empty.action" type="button" @click="empty.action" class="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline">{{ empty.button }}</button>
          </div>

          <ul v-else class="divide-y divide-gray-100 dark:divide-gray-800 transition-opacity" :class="store.loading ? 'opacity-60' : ''">
            <li v-for="job in store.jobs" :key="job.id" :data-job-id="job.id">
              <JobListItem :job="job" :selected="job.id === store.selectedId" @select="open"
                @toggle-bookmark="toggle($event, 'bookmarked')" @toggle-ignore="toggle($event, 'ignored')" />
            </li>
          </ul>

          <!-- Scrolling near the end loads the next page. The sentinel sits just under the last job. -->
          <div v-if="store.hasMore" ref="sentinel" class="py-3 text-center text-xs text-gray-400 dark:text-gray-500" aria-live="polite">
            <button v-if="store.loadMoreError" type="button" @click="store.loadMore"
              class="font-semibold text-blue-600 dark:text-blue-400 hover:underline">Could not load more jobs. Try again</button>
            <span v-else class="inline-flex items-center gap-2">
              <svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"/></svg>
              Loading more jobs
            </span>
          </div>
        </div>
      </div>

      <!-- Detail, beside the list on wide screens -->
      <div class="hidden lg:block flex-1 min-w-0 min-h-0 bg-white dark:bg-[#1C2128]">
        <JobDetail :job="store.selected" :loading="store.detailLoading" :error="store.detailError" :applying="applying"
          @toggle-applied="toggleApplied" @toggle-bookmark="toggle($event, 'bookmarked')" @toggle-ignore="toggle($event, 'ignored')"
          @retry="store.select(store.selectedId)" @open-company="$emit('open-company', $event)" @open-ai-settings="$emit('open-ai-settings')" />
      </div>
    </div>

    <!-- Detail, over the list on small screens -->
    <div v-if="mobileOpen" class="fixed inset-0 z-40 lg:hidden bg-white dark:bg-[#1C2128]">
      <JobDetail :job="store.selected" :loading="store.detailLoading" :error="store.detailError" :applying="applying" show-back
        @toggle-applied="toggleApplied" @toggle-bookmark="toggle($event, 'bookmarked')" @toggle-ignore="toggle($event, 'ignored')"
        @retry="store.select(store.selectedId)" @back="mobileOpen = false" @open-company="$emit('open-company', $event)" @open-ai-settings="$emit('open-ai-settings')" />
    </div>

    <!-- Ignoring moves a job out of the Not actioned list, so it asks first. Restoring one does not. -->
    <ConfirmModal :open="Boolean(ignoring)" title="Ignore this job?" :danger="false" confirm-text="Ignore job"
      message="It will be marked as ignored and leave your Not actioned jobs. You can bring it back any time."
      @cancel="ignoring = null" @confirm="confirmIgnore">
      <template #icon>
        <svg class="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/>
        </svg>
      </template>
      <template #subject>
        <JobAvatar v-if="ignoring" :name="ignoring.company_name" :src="ignoring.company_logo_url" />
        <span v-if="ignoring" class="min-w-0 text-left">
          <span class="block text-sm font-semibold text-gray-900 dark:text-white truncate">{{ ignoring.title || 'Untitled job' }}</span>
          <span v-if="ignoring.company_name" class="block text-xs text-gray-500 dark:text-gray-400 truncate">{{ ignoring.company_name }}</span>
        </span>
      </template>
    </ConfirmModal>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useFetchedJobsStore } from 'stores/fetchedJobs.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import JobListItem from './JobListItem.vue'
import JobDetail from './JobDetail.vue'
import JobAvatar from './JobAvatar.vue'
import ConfirmModal from 'controllers/vue_components/shared/ConfirmModal.vue'

defineEmits(['go-searches', 'open-company', 'open-ai-settings'])

const store = useFetchedJobsStore()
const flash = ref(null)
const applying = ref(false)
const mobileOpen = ref(false)
const ignoring = ref(null) // the job waiting for a yes before it is ignored
const scroller = ref(null)
const sentinel = ref(null)

// Infinite scroll: when the sentinel under the last job comes within reach, load the next page.
// An observer only reports changes, so if a page does not push the sentinel out of view it is
// observed again (watch below) to get another report. A failed page stops the chain until Try
// again, so an outage cannot loop.
let observer = null

function watchSentinel() {
  observer?.disconnect()
  if (!sentinel.value || !scroller.value || typeof IntersectionObserver === 'undefined') return

  observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) store.loadMore()
  }, { root: scroller.value, rootMargin: '0px 0px 300px 0px' })
  observer.observe(sentinel.value)
}

watch(() => [store.jobs.length, store.hasMore, store.loading], () => nextTick(watchSentinel), { flush: 'post' })
onBeforeUnmount(() => observer?.disconnect())
onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))

// Up and down arrows move to the previous or next job in the list, like in a mail client. They are left alone while
// typing in a field, with a modifier held, while a menu or dialog is open, so they never take a key someone else needs.
async function onKey(event) {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
  if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
  if (event.target.closest?.('input, textarea, select, [contenteditable="true"], [role="listbox"], [role="dialog"]')) return
  if (store.jobs.length === 0 || ignoring.value || !isWide()) return // on a small screen a job opens over the list, which a key press should not do

  event.preventDefault()
  const index = store.jobs.findIndex((job) => job.id === store.selectedId)
  const target = index === -1 ? 0 : index + (event.key === 'ArrowDown' ? 1 : -1)

  // Past the last loaded job: bring in the next page first, if there is one.
  if (target >= store.jobs.length && store.hasMore) {
    await store.loadMore()
    if (target >= store.jobs.length) return
  }
  if (target < 0 || target >= store.jobs.length) return

  const job = store.jobs[target]
  open(job.id)
  await nextTick()
  scroller.value?.querySelector(`[data-job-id="${job.id}"]`)?.scrollIntoView({ block: 'nearest' })
}

const isWide = () => window.matchMedia?.('(min-width: 1024px)').matches

function open(id) {
  store.pinnedId = null
  store.select(id)
  if (!isWide()) mobileOpen.value = true
}

// On a wide screen there is always a job open beside the list, so it never looks empty.
watch(() => store.jobs, (jobs) => {
  if (store.refreshing || !isWide() || jobs.length === 0) return
  if (store.pinnedId && store.pinnedId === store.selectedId) return // opened from a link: leave it open
  if (!jobs.some((job) => job.id === store.selectedId)) store.select(jobs[0].id)
})

// Bookmarks or ignores a job. When that takes it out of the list, the panel over the list on a
// small screen closes, since the job is no longer there to read.
function toggle(job, mark) {
  if (mark === 'ignored' && !job.ignored_at) ignoring.value = job
  else save(job, mark)
}

function confirmIgnore() {
  const job = ignoring.value
  ignoring.value = null
  if (job) save(job, 'ignored')
}

async function save(job, mark) {
  const result = await store.setMark(job.id, mark, !job[`${mark}_at`])
  if (!result.ok) flash.value?.show(result.message, 'alert')
  else if (result.removed && !isWide()) mobileOpen.value = false
}

// What an empty list says, which depends on the view and on whether other filters are on.
const empty = computed(() => {
  const view = store.filters.view
  const clear = { button: 'Clear filters', action: store.clearFilters }
  const show = (v, button) => ({ button, action: () => store.setFilters({ view: v }) })

  if (hasOtherFilters.value) return { title: 'No jobs match', body: 'Try a different search or clear the filters.', ...clear }
  if (view === 'bookmarked') return { title: 'No bookmarked jobs', body: 'Hover over a job and click the bookmark to keep it here.', ...show('all', 'See all jobs') }
  if (view === 'ignored') return { title: 'Nothing ignored', body: 'Jobs you ignore end up here.', ...show('all', 'See all jobs') }
  if (view === 'active') return { title: 'Nothing left to action', body: 'Every job is already bookmarked or ignored.', ...show('all', 'See all jobs') }
  return { title: 'No jobs yet', body: 'Run a search from the Timelydo Jobs extension and your jobs will show up here.' }
})
const hasOtherFilters = computed(() => {
  const { q, job_title_id, job_location_id, status, posted, ai_resume, poster, platform } = store.filters
  return Boolean(q.trim() || job_title_id || job_location_id || status !== 'all' || posted || ai_resume !== 'all' || poster !== 'all' || platform !== 'all')
})

async function toggleApplied() {
  const job = store.selected
  if (!job) return

  applying.value = true
  const result = await store.setApplied(job.id, !job.applied_at)
  applying.value = false
  if (!result.ok) flash.value?.show(result.message, 'alert')
}

onMounted(async () => {
  await store.fetchJobs()
  nextTick(watchSentinel)
})
</script>
